import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { apiConfig } from "../../config";
import ApiProductReviews from "./product-reviews-api";
import { Keys } from "../../common-interfaces/quiries-keys/keysValues";
import { getKey } from "../../common-interfaces/quiries-keys/queries-keys";
import { IGetAllProductReviewsParams } from "./interfaces";

export const useProductReviews = () => new ApiProductReviews(apiConfig());

export const useGetAllProductReviewsQuery = ({
  params,
  enabled = true,
}: {
  params: IGetAllProductReviewsParams;
  enabled?: boolean;
}) => {
  const productReviews = useProductReviews();
  return useQuery(
    [
      ...getKey(Keys.getAllProductReviews),
      Object.keys(params),
      Object.values(params),
    ],
    () => productReviews.getReviews({ ...params }),
    {
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
      enabled: enabled,
    },
  );
};
export const useGetAllProductReviewsInfiniteQuery = ({
  params,
  enabled = true,
}: {
  params: IGetAllProductReviewsParams;
  enabled?: boolean;
}) => {
  const productReviews = useProductReviews();
  return useInfiniteQuery(
    [
      ...getKey(Keys.getAllProductReviews),
      Object.keys(params),
      Object.values(params),
      "InfiniteQuery",
    ],
    ({ pageParam = { ...params, page: 1 } }) =>
      productReviews.getReviews(pageParam),
    {
      getNextPageParam: (lastPage) => {
        const nextPage = lastPage.result.meta.current_page + 1;
        const totalPages = lastPage.result.meta.last_page;
        return nextPage <= totalPages ? { ...params, page: nextPage } : null;
      },
      enabled: enabled,
    },
  );
};

export const useSubmitToggleUnusefulQuery = ({
  onSuccess,
  onError,
}: {
  onSuccess: (res: any) => void;
  onError: (x: any) => void;
}) => {
  const productReviews = useProductReviews();

  return useMutation(productReviews.postToggleUnuseful, {
    onError,
    onSuccess,
  });
};
export const useSubmitToggleUsefulQuery = ({
  onSuccess,
  onError,
}: {
  onSuccess: (res: any) => void;
  onError: (x: any) => void;
}) => {
  const productReviews = useProductReviews();

  return useMutation(productReviews.postToggleUseful, {
    onError,
    onSuccess,
  });
};

export const useManageProductReview = ({
  onSuccess,
  onError,
  params,
}: {
  onSuccess: (res: any) => void;
  onError: (x: any) => void;
  params: IGetAllProductReviewsParams;
}) => {
  const productReviews = useProductReviews();
  const allReviews = useGetAllProductReviewsInfiniteQuery({ params });
  const OnSuccess = (res: any) => {
    allReviews.refetch();
    return onSuccess(res);
  };
  return useMutation(productReviews.manageProductReview, {
    onError,
    onSuccess: OnSuccess,
  });
};
